import { useCallback, useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'

interface Props {
  meetingId: number
  sectionId: number
  initialNotes: string | null
  readOnly?: boolean
  label?: string
  placeholder?: string
  minRows?: number
  debounceMs?: number
}

const DEFAULT_DEBOUNCE_MS = 1500

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

export default function SectionNotes({
  meetingId,
  sectionId,
  initialNotes,
  readOnly = false,
  label = 'Notes',
  placeholder = 'Capture notes for this section…',
  minRows = 4,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: Props) {
  const [value, setValue] = useState<string>(initialNotes ?? '')
  const [status, setStatus] = useState<SaveStatus>('idle')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSavedRef = useRef<string>(initialNotes ?? '')
  const pendingValueRef = useRef<string | null>(null)
  const meetingIdRef = useRef(meetingId)
  const sectionIdRef = useRef(sectionId)

  const persist = useCallback((notes: string, mId: number, sId: number) => {
    pendingValueRef.current = null
    setStatus('saving')
    router.patch(`/meetings/${mId}/sections/${sId}/update_notes`, { notes }, {
      preserveScroll: true,
      preserveState: true,
      only: ['autosave_ack'],
      onSuccess: () => {
        lastSavedRef.current = notes
        setStatus('saved')
        setTimeout(() => setStatus(prev => prev === 'saved' ? 'idle' : prev), 2000)
      },
      onError: () => setStatus('error'),
    })
  }, [])

  // Reset local state when the section changes underneath us (e.g. the
  // facilitator jumps to a different section). Flush any pending save for the
  // outgoing section before swapping in the new section's initial notes.
  useEffect(() => {
    meetingIdRef.current = meetingId
    sectionIdRef.current = sectionId
  }, [meetingId, sectionId])

  useEffect(() => {
    setValue(initialNotes ?? '')
    lastSavedRef.current = initialNotes ?? ''
    setStatus('idle')
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      if (pendingValueRef.current !== null) {
        persist(pendingValueRef.current, meetingIdRef.current, sectionIdRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId])

  const scheduleSave = useCallback((notes: string) => {
    pendingValueRef.current = notes
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      persist(notes, meetingIdRef.current, sectionIdRef.current)
    }, debounceMs)
  }, [persist, debounceMs])

  const handleChange = (next: string) => {
    setValue(next)
    if (next !== lastSavedRef.current) {
      scheduleSave(next)
    } else {
      // Value rolled back to the saved baseline — cancel any pending save.
      pendingValueRef.current = null
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }

  const handleBlur = () => {
    if (value !== lastSavedRef.current) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      persist(value, meetingIdRef.current, sectionIdRef.current)
    }
  }

  if (readOnly) {
    return (
      <div className="space-y-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-text-muted">{label}</h4>
        {value.trim().length > 0 ? (
          <p className="text-sm text-text-primary whitespace-pre-wrap">{value}</p>
        ) : (
          <p className="text-sm text-text-muted italic">No notes were captured for this section.</p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label htmlFor={`section-notes-${sectionId}`} className="text-xs font-bold uppercase tracking-wider text-text-muted">
          {label}
        </label>
        <span aria-live="polite" className="text-xs text-text-muted min-h-[1rem]">
          {status === 'saving' && 'Saving…'}
          {status === 'saved' && 'Saved'}
          {status === 'error' && <span className="text-pinnacle-red">Save failed — retry</span>}
        </span>
      </div>
      <textarea
        id={`section-notes-${sectionId}`}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        rows={minRows}
        className="w-full rounded-lg border border-stone/30 bg-white px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red/40"
      />
    </div>
  )
}
